import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { MagnifyingGlassIcon, UserIcon, PhoneIcon, EnvelopeIcon, ChatBubbleLeftIcon, UserGroupIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline'
import { useAuth0 } from '@auth0/auth0-react'
import { useUsersApi, User as ApiUser } from '../services/usersApi'
import LoadingSpinner from '../components/LoadingSpinner'
import toast from 'react-hot-toast'
import { formatPhoneNumberForDisplay, formatPhoneNumberE164, getMembersWithPhoneNumbers, createSMSUrl } from '../utils/phoneUtils'

interface Member {
  userId: string
  email: string
  firstName: string
  lastName: string
  name: string
  phone?: string
  mobile?: string
  role: 'user' | 'admin'
  status: 'pending' | 'approved' | 'denied'
  isApproved: boolean
  joinedYear?: string
  createdAt: string
  updatedAt: string
}

export default function MembersPage() {
  const { user } = useAuth0()
  const navigate = useNavigate()
  const usersApi = useUsersApi()
  const [members, setMembers] = useState<ApiUser[]>([])
  const [filteredMembers, setFilteredMembers] = useState<ApiUser[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [filterRole, setFilterRole] = useState<'all' | 'user' | 'admin'>('all')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadMembers()
  }, [])

  const sendSMSGreeting = (member: ApiUser) => {
    const phoneNumber = member.phone || member.mobile
    if (!phoneNumber) {
      toast.error('No phone number available for this member')
      return
    }
    
    const message = 'Greetings brother railbird!'
    const smsUrl = createSMSUrl(phoneNumber, message)
    
    if (!smsUrl) {
      toast.error('Invalid phone number format')
      return
    }
    
    // Open SMS app
    window.location.href = smsUrl
    toast.success(`Opening SMS to ${member.name || `${member.firstName} ${member.lastName}`.trim()}`)
  }

  const copyGroupNumbers = async () => {
    const membersWithPhones = getMembersWithPhoneNumbers(filteredMembers)
    
    if (membersWithPhones.length === 0) {
      toast.error('No members with valid phone numbers found')
      return
    }
    
    const phoneNumbers = membersWithPhones.map(member => member.phone || member.mobile).filter(Boolean) as string[]
    const formattedNumbers = phoneNumbers.map(formatPhoneNumberE164).filter(Boolean).join(';')
    
    try {
      await navigator.clipboard.writeText(formattedNumbers)
      toast.success(`Copied ${membersWithPhones.length} phone numbers to clipboard! Now paste into your messaging app.`)
    } catch (error) {
      // Fallback for browsers that don't support clipboard API
      const textArea = document.createElement('textarea')
      textArea.value = formattedNumbers
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand('copy')
      document.body.removeChild(textArea)
      toast.success(`Copied ${membersWithPhones.length} phone numbers to clipboard! Now paste into your messaging app.`)
    }
  }

  const loadMembers = async () => {
    try {
      setLoading(true)
      const data = await usersApi.getUsers()
      
      // Ensure all required fields are present
      const validMembers = data.users.map(member => {
        const processed = {
          ...member,
          userId: member.userId || member.sub || member.id || '',
          name: member.name || `${member.firstName} ${member.lastName}`.trim(),
          role: member.role || 'user',
          status: member.status || 'approved',
          isApproved: member.status === 'active' || member.isActive,
          phone: member.mobile || member.phone, // Map mobile to phone for consistency
          mobile: member.mobile
        };
        
        
        return processed;
      })
      
      setMembers(validMembers)
      setFilteredMembers(validMembers)
    } catch (error) {
      toast.error('Failed to load members')
      setMembers([])
      setFilteredMembers([])
    } finally {
      setLoading(false)
    }
  }

  // Filter and search members
  useEffect(() => {
    let filtered = members

    // Filter by role
    if (filterRole !== 'all') {
      filtered = filtered.filter(member => member.role === filterRole)
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(member =>
        (member.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (member.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        member.phone?.includes(searchTerm) ||
        member.mobile?.includes(searchTerm)
      )
    }

    setFilteredMembers(filtered)
  }, [members, searchTerm, filterRole])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-6"
      >
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Members Directory</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
            Connect with fellow Midwest Railbirds members
          </p>
        </div>
        {/* Copy Phone Numbers Button */}
        {getMembersWithPhoneNumbers(filteredMembers).length > 0 && (
          <div className="relative">
            <button
              onClick={copyGroupNumbers}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200 shadow-sm"
              title={`Copy ${getMembersWithPhoneNumbers(filteredMembers).length} phone numbers to clipboard for pasting into a new message`}
            >
              <ClipboardDocumentIcon className="w-5 h-5" />
              <span className="hidden sm:inline">Copy Numbers</span>
              <span className="sm:hidden">{getMembersWithPhoneNumbers(filteredMembers).length}</span>
            </button>
          </div>
        )}
      </div>

      {/* Search and Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6 transition-colors duration-300">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search Input */}
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search members by name, email, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
            />
          </div>

          {/* Role Filter */}
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value as 'all' | 'user' | 'admin')}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="all">All Members</option>
            <option value="user">Members</option>
            <option value="admin">Admins</option>
          </select>
        </div>

        <div className="mt-4 text-sm text-gray-600 dark:text-gray-300">
          Showing {filteredMembers.length} of {members.length} members
        </div>
      </div>

      {/* Members Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredMembers.map((member) => (
          <motion.div
            key={member.userId}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6 transition-colors duration-300 cursor-pointer hover:shadow-lg relative"
            onClick={(e) => {
              e.preventDefault();
              navigate(`/members/${member.userId}`);
            }}
          >
            {/* SMS Message Icon */}
            {(member.phone || member.mobile) && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  sendSMSGreeting(member);
                }}
                className="absolute top-4 right-4 p-2 bg-green-100 dark:bg-green-900 hover:bg-green-200 dark:hover:bg-green-800 rounded-full transition-colors duration-200 group"
                title={`Send SMS to ${member.name || `${member.firstName} ${member.lastName}`.trim()}`}
              >
                <ChatBubbleLeftIcon className="w-4 h-4 text-green-600 dark:text-green-400 group-hover:text-green-700 dark:group-hover:text-green-300" />
              </button>
            )}
            {/* Member Avatar */}
            <div className="flex items-center space-x-4 mb-4">
              <div className="flex-shrink-0">
                {member.avatar ? (
                  <img
                    src={member.avatar}
                    alt={member.name || `${member.firstName} ${member.lastName}`.trim()}
                    className="w-12 h-12 rounded-full object-cover border-2 border-primary-200 dark:border-primary-700"
                    onError={(e) => {
                      // Fallback to initials if image fails to load
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const fallback = target.nextElementSibling as HTMLElement;
                      if (fallback) fallback.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div 
                  className={`w-12 h-12 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center ${member.avatar ? 'hidden' : 'flex'}`}
                  style={member.avatar ? { display: 'none' } : {}}
                >
                  <span className="text-sm font-semibold text-primary-600 dark:text-primary-400">
                    {(() => {
                      const name = member.name || `${member.firstName} ${member.lastName}`.trim();
                      const nameParts = name.split(' ').filter(part => part.length > 0);
                      if (nameParts.length >= 2) {
                        return `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`.toUpperCase();
                      } else if (nameParts.length === 1) {
                        return nameParts[0].substring(0, 2).toUpperCase();
                      } else {
                        return 'MR'; // Default for Midwest Railbirds
                      }
                    })()}
                  </span>
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white truncate">
                  {member.name || `${member.firstName} ${member.lastName}`.trim()}
                </h3>
                <div className="flex items-center space-x-2">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    member.role === 'admin' 
                      ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' 
                      : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                  }`}>
                    {member.role === 'admin' ? 'Admin' : 'Member'}
                  </span>
                  {member.joinedYear && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      Since {member.joinedYear}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Contact Info */}
            <div className="space-y-2">
              <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-300">
                <EnvelopeIcon className="w-4 h-4" />
                <span className="truncate">{member.email}</span>
              </div>
              {member.phone && (
                <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-300">
                  <PhoneIcon className="w-4 h-4" />
                  <span>{formatPhoneNumberForDisplay(member.phone)}</span>
                </div>
              )}
            </div>

            {/* Admin Actions */}
            {user?.role === 'admin' && (
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/members/${member.userId}`);
                  }}
                  className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium"
                >
                  View Profile
                </button>
              </div>
            )}
          </motion.div>
        ))}
      </div>

      {/* Empty State */}
      {filteredMembers.length === 0 && !loading && (
        <div className="text-center py-12">
          <UserIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No members found</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Try adjusting your search or filter criteria.
          </p>
        </div>
      )}
      </motion.div>
    </div>
  )
}
